import { logger } from "@/lib/logger";
import type { ChatDoneEvent } from "@/services/chatStreamService";
import { isStreamContext, type StreamContext } from "@/lib/inFlightStreamState";
import { IN_FLIGHT_STREAM_TTL_MS } from "@/lib/persistedInFlightStream";

export const pendingStreamRecoveryStorageKey = (userId: string): string =>
  `chat:pendingStreamRecovery:${userId}`;

export type PendingStreamRecoveryPhase =
  | "streaming"
  | "persisting_done"
  | "persisting_error";

export type PendingStreamRecovery = {
  generationId: string;
  conversationId: string;
  userId: string;
  tenantId: string;
  lastChunkSeq: number;
  phase: PendingStreamRecoveryPhase;
  streamContext: StreamContext | null;
  doneEvent: ChatDoneEvent | null;
  errorMessage: string | null;
  inFlightHumanMessageId: string | null;
  inFlightHumanMessagePersisted: boolean;
  persistedAt: number;
};

type PendingStreamRecoveryMap = Record<string, PendingStreamRecovery>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringRecord = (value: unknown): value is Record<string, string> => {
  if (!isRecord(value)) return false;
  return Object.values(value).every((entry) => typeof entry === "string");
};

const isDoneEvent = (value: unknown): value is ChatDoneEvent => {
  if (!isRecord(value)) return false;
  return (
    typeof value.text === "string" &&
    Array.isArray(value.references) &&
    typeof value.chainOfThoughtMessage === "string" &&
    typeof value.revisedPrompt === "string" &&
    isStringRecord(value.lemmatizedText) &&
    typeof value.statusShort === "string" &&
    typeof value.statusLong === "string"
  );
};

const isPendingStreamRecovery = (
  value: unknown
): value is PendingStreamRecovery => {
  if (!isRecord(value)) return false;
  return (
    typeof value.generationId === "string" &&
    typeof value.conversationId === "string" &&
    typeof value.userId === "string" &&
    typeof value.tenantId === "string" &&
    typeof value.lastChunkSeq === "number" &&
    (value.phase === "streaming" ||
      value.phase === "persisting_done" ||
      value.phase === "persisting_error") &&
    (value.streamContext === null || isStreamContext(value.streamContext)) &&
    (value.doneEvent === null || isDoneEvent(value.doneEvent)) &&
    (value.errorMessage === null || typeof value.errorMessage === "string") &&
    (value.inFlightHumanMessageId === null ||
      typeof value.inFlightHumanMessageId === "string") &&
    typeof value.inFlightHumanMessagePersisted === "boolean" &&
    typeof value.persistedAt === "number"
  );
};

const readMap = (userId: string): PendingStreamRecoveryMap => {
  if (typeof localStorage === "undefined" || !userId) {
    return {};
  }

  const raw = localStorage.getItem(pendingStreamRecoveryStorageKey(userId));
  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return {};

    const result: PendingStreamRecoveryMap = {};
    for (const [generationId, entry] of Object.entries(parsed)) {
      if (isPendingStreamRecovery(entry)) {
        result[generationId] = entry;
      }
    }
    return result;
  } catch {
    return {};
  }
};

const writeMap = (userId: string, map: PendingStreamRecoveryMap): void => {
  if (typeof localStorage === "undefined" || !userId) {
    return;
  }

  const key = pendingStreamRecoveryStorageKey(userId);
  try {
    if (Object.keys(map).length === 0) {
      localStorage.removeItem(key);
      return;
    }

    localStorage.setItem(key, JSON.stringify(map));
  } catch (error) {
    logger.warn("Failed to persist pending stream recovery to localStorage", {
      operation: "writePendingStreamRecoveryMap",
      storageKey: key,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  }
};

export const evictExpiredPendingStreams = (
  userId: string,
  ttlMs: number = IN_FLIGHT_STREAM_TTL_MS
): void => {
  const map = readMap(userId);
  const now = Date.now();
  let changed = false;

  for (const [generationId, entry] of Object.entries(map)) {
    if (now - entry.persistedAt > ttlMs) {
      delete map[generationId];
      changed = true;
    }
  }

  if (changed) {
    writeMap(userId, map);
  }
};

export const upsertPendingStream = (
  userId: string,
  entry: PendingStreamRecovery
): void => {
  if (!userId || entry.userId !== userId) {
    return;
  }

  evictExpiredPendingStreams(userId);
  const map = readMap(userId);
  map[entry.generationId] = {
    ...entry,
    persistedAt: Date.now(),
  };
  writeMap(userId, map);
};

export const removePendingStream = (
  userId: string,
  generationId: string
): void => {
  if (!userId) {
    return;
  }

  const map = readMap(userId);
  if (!(generationId in map)) {
    return;
  }

  delete map[generationId];
  writeMap(userId, map);
};

export const getPendingStream = (
  userId: string,
  generationId: string
): PendingStreamRecovery | null => {
  evictExpiredPendingStreams(userId);
  return readMap(userId)[generationId] ?? null;
};

export const listPendingStreams = (
  userId: string
): PendingStreamRecovery[] => {
  evictExpiredPendingStreams(userId);
  return Object.values(readMap(userId)).filter(
    (entry) => entry.userId === userId
  );
};

export const clearAllPendingStreams = (userId: string): void => {
  if (typeof localStorage === "undefined" || !userId) {
    return;
  }
  localStorage.removeItem(pendingStreamRecoveryStorageKey(userId));
};
